export type MeterStatusSplit = {
  isLate: boolean;
  vendor: string[];
};

const normalizeStatus = (status: unknown): string[] => {
  if (!Array.isArray(status)) {
    return [];
  }
  return status
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const splitMeterStatus = (
  status: unknown,
  isLate: boolean | null | undefined
): MeterStatusSplit => {
  const list = normalizeStatus(status);
  return {
    isLate: Boolean(isLate),
    vendor: list.filter((item) => item.toLowerCase() !== "late"),
  };
};

export const statusSortValue = (
  status: unknown,
  isLate: boolean | null | undefined
): number => {
  const split = splitMeterStatus(status, isLate);
  if (split.vendor.length > 0) {
    return 2 + split.vendor.length;
  }
  return split.isLate ? 1 : 0;
};

export const formatBatteryValue = (
  value: number | null | undefined
): string => {
  if (value == null || Number.isNaN(value)) {
    return "N/A";
  }
  return value.toFixed(2);
};

type MeterMedia = "electricity" | "water" | "heat" | "all" | string;

type MeterSnapshotRecord = {
  media?: MeterMedia | null;
  unit?: string | null;
  latest_readout?: {
    values?: Record<string, number> | null;
    timestamp?: string | null;
    status?: unknown;
    fields?: Record<string, unknown>;
    is_late?: boolean;
  } | null;
  timestamp?: string | null;
};

export const primaryMetricForMedia = (media: MeterMedia | null | undefined): string | null => {
  switch (media) {
    case "electricity":
      return "active";
    case "water":
      return "water";
    case "heat":
      return "heat_kwh";
    default:
      return null;
  }
};

export const getSnapshotValue = (
  record: MeterSnapshotRecord | null | undefined,
  metric: string
): number | null => {
  const nestedValues = record?.latest_readout?.values;
  if (!nestedValues || typeof nestedValues !== "object") {
    return null;
  }
  const nestedValue = nestedValues[metric];
  return typeof nestedValue === "number" && Number.isFinite(nestedValue) ? nestedValue : null;
};

export const getPrimarySnapshotValue = (
  record: MeterSnapshotRecord | null | undefined
): number | null => {
  if (record?.media === "heat") {
    if (record.unit === "GJ") {
      return getSnapshotValue(record, "heat_gj");
    }
    return getSnapshotValue(record, "heat_kwh");
  }

  const primaryMetric = primaryMetricForMedia(record?.media);
  if (!primaryMetric) {
    return null;
  }
  return getSnapshotValue(record, primaryMetric);
};

export const getReadoutTimestamp = (
  record: MeterSnapshotRecord | null | undefined
): string | null => {
  return record?.latest_readout?.timestamp ?? record?.timestamp ?? null;
};

export const getReadoutStatus = (
  record: MeterSnapshotRecord | null | undefined
): unknown => record?.latest_readout?.status;

export const getReadoutIsLate = (
  record: MeterSnapshotRecord | null | undefined
): boolean => Boolean(record?.latest_readout?.is_late);

export const getReadoutFields = (
  record: MeterSnapshotRecord | null | undefined
): Record<string, unknown> => {
  const fields = record?.latest_readout?.fields;
  return fields && typeof fields === "object" ? fields : {};
};
